import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getCurrentUser,
  getIdToken,
  signIn,
  signOut,
  getUserClaims,
  wasAuthenticated,
  forgotPassword as cognitoForgotPassword,
  confirmPassword as cognitoConfirmPassword,
  confirmRegistration as cognitoConfirmRegistration,
  resendConfirmationCode as cognitoResendCode
} from '../services/cognito';
import { api } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const cognitoUser = getCurrentUser();
        const hadSession = wasAuthenticated();

        if (cognitoUser) {
          const claims = await getUserClaims();
          setUser(claims);
          setIsAuthenticated(true);
          console.log('[Auth] Session restored from Cognito');
        } else if (hadSession) {
          // Session marker exists but no user - try getting session directly
          // This handles the case where module state was reset but storage is intact
          console.log('[Auth] Session marker exists, attempting to restore...');
          try {
            const claims = await getUserClaims();
            setUser(claims);
            setIsAuthenticated(true);
            console.log('[Auth] Session restored via getUserClaims');
          } catch (innerError) {
            console.log('[Auth] Could not restore session:', innerError.message);
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          console.log('[Auth] No session found');
        }
      } catch (error) {
        console.log('[Auth] No valid session found:', error.message);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Session expiry warning — only surfaces when the silent token refresh in
  // getIdToken() actually fails. Under normal conditions getIdToken() rolls
  // the ID token using the long-lived refresh token before expiry, so we
  // should never warn the user mid-session unless the refresh itself fails
  // (refresh token expired / network down / Cognito error). Calling
  // getIdToken() here piggybacks on its built-in refresh logic.
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkExpiry = async () => {
      try {
        await getIdToken();
        setSessionWarning(false);
      } catch (error) {
        setSessionWarning(true);
      }
    };

    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Listen for session expired events from API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:session_expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session_expired', handleSessionExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    const session = await signIn(email, password);
    // Extract claims directly from the session returned by signIn
    // This avoids the race condition where getCurrentUser() returns null
    const idToken = session.getIdToken();
    const claims = {
      email: idToken.payload.email,
      userId: idToken.payload.sub,
      tenantId: idToken.payload['custom:tenant_id'],
      role: idToken.payload['custom:role'],
      emailVerified: idToken.payload.email_verified,
    };
    setUser(claims);
    setIsAuthenticated(true);
    return session;
  }, []);

  const logout = useCallback(() => {
    signOut();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const register = useCallback(async (email, password, tenantName) => {
    // Backend handles Cognito SignUp with controlled custom attributes
    const response = await api.post('/auth/register', {
      email,
      password,
      tenant_name: tenantName
    });
    return response.data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    await cognitoForgotPassword(email);
  }, []);

  const confirmPasswordReset = useCallback(async (email, code, newPassword) => {
    await cognitoConfirmPassword(email, code, newPassword);
  }, []);

  const confirmEmail = useCallback(async (email, code) => {
    await cognitoConfirmRegistration(email, code);
  }, []);

  const resendVerificationCode = useCallback(async (email) => {
    await cognitoResendCode(email);
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    sessionWarning,
    login,
    logout,
    register,
    forgotPassword,
    confirmPasswordReset,
    confirmEmail,
    resendVerificationCode,
    dismissSessionWarning: () => setSessionWarning(false),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
